import React, { useState } from "react";
import { addWeeks, startOfDay } from "date-fns";

import { MeetingList } from "./components/MeetingList";

import {
  Stack,
  Box,
  SelectChangeEvent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Typography,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SyncIcon from "@mui/icons-material/Sync";
import { FilterTypes, Filters, defaultFilters } from "./components/Filters";
import { MeetingDetailsContainer } from "./components/meeting_details/MeetingDetails";
import { useBoundStore } from "../../store/store";
import { useIsMobile } from "../../hooks/useIsMobile";
import { Meeting, isMissingData } from "../../model/meeting";
import { hostLabel } from "../../model/host";
import { MeetingResource } from "../../api/meetings";
import { Partner, PartnerResource } from "../../api/partners";
import { stableKeyOf } from "./helpers";

const SYNC_COOLDOWN_MS = 60_000;
const SYNC_COOLDOWN_KEY = "calendarSyncLastTriggered";

function isActiveFilters(filters: FilterTypes): boolean {
  return (
    (filters.search ?? "") !== "" ||
    (filters.meetingType ?? "All") !== "All" ||
    (filters.status ?? "All") !== "All" ||
    (filters.hostId ?? "All") !== "All" ||
    (filters.roomKey ?? "All") !== "All" ||
    (filters.partnerId ?? "All") !== "All"
  );
}

// Instance override wins; otherwise the key set on the series applies.
function effectiveRoomKey(meeting: Meeting): string | undefined {
  return (
    meeting.metadata.continuousRoomKey ||
    meeting.recurringMeeting?.metadata.continuousRoomKey
  );
}

function getCooldownRemaining(): number {
  const last = localStorage.getItem(SYNC_COOLDOWN_KEY);
  if (!last) return 0;
  const elapsed = Date.now() - parseInt(last, 10);
  return Math.max(0, SYNC_COOLDOWN_MS - elapsed);
}

export const MeetingListView = () => {
  const meetings = useBoundStore((state) => state.meetings);
  const hostsById = useBoundStore((state) => state.hostsById);
  const staffHostsById = useBoundStore((state) => state.staffHostsById);
  const loadMeetings = useBoundStore((state) => state.loadMeetings);
  const loadMeetingTypes = useBoundStore((state) => state.loadMeetingTypes);
  const isLoading = useBoundStore((state) => state.isLoading);
  const meetingsStart = useBoundStore((state) => state.start);
  const meetingsEnd = useBoundStore((state) => state.end);

  const isMobile = useIsMobile();
  const [filteredMeetings, setFilteredMeetings] = useState<Meeting[]>([]);
  const [filters, setFilters] = useState<FilterTypes>(defaultFilters);
  const [selectedMeetingId, setSelectedMeetingId] = useState<string | null>(
    null,
  );
  const [hasLoaded, setHasLoaded] = useState(false);

  const [partners, setPartners] = useState<Partner[]>([]);
  // Gcal event ids of the selected partner's partner-only meetings, loaded
  // lazily when the Partner filter is used (null while loading / not loaded).
  const [scopedGcalIds, setScopedGcalIds] = useState<Set<string> | null>(null);
  // Gcal event id → partner name, across ALL partners, for the "Private" badge
  // on the list cards (one cached scoped-meetings request per partner).
  const [privateByGcalId, setPrivateByGcalId] = useState<Map<string, string>>(
    new Map(),
  );

  const [isSyncing, setIsSyncing] = useState(false);
  const [cooldownRemaining, setCooldownRemaining] =
    useState(getCooldownRemaining);
  const [syncSnackbar, setSyncSnackbar] = useState<{
    open: boolean;
    severity: "success" | "error";
    message: string;
  }>({ open: false, severity: "success", message: "" });

  // Details open in a dialog for the clicked meeting only. If a reload or
  // filter change removes it from the visible list, the dialog just closes.
  const activeFilters = isActiveFilters(filters);
  const activeList = activeFilters ? filteredMeetings : meetings;
  const selectedMeeting = activeList.find((m) => m.id === selectedMeetingId);

  function handleSelectMeeting(mId: string) {
    setSelectedMeetingId(mId);
  }

  function handleCloseDetails() {
    setSelectedMeetingId(null);
    // Partner associations may have been edited inside the dialog; refresh the
    // badge data (caches were invalidated by the updates, so this is fresh).
    loadPartnerData();
  }

  function handleUpdateFilter(
    value: string | undefined,
    filter: keyof FilterTypes,
  ) {
    setFilters({ ...filters, [filter]: value });
  }

  React.useEffect(() => {
    async function loadData() {
      const now = new Date();
      const start = startOfDay(now);
      const end = addWeeks(start, 4);
      await Promise.all([loadMeetings(start, end), loadMeetingTypes()]);
      setHasLoaded(true);
    }

    loadData();
    loadPartnerData();
  }, []);

  // Partner filter options + the private-badge map; on failure the filter and
  // badges simply don't render. Re-run when the details dialog closes so badge
  // edits made inside it show up immediately.
  async function loadPartnerData() {
    try {
      const ps = await PartnerResource.getListCached();
      setPartners(ps);
      const entries = await Promise.all(
        ps.map((p) =>
          PartnerResource.getScopedMeetingsCached(p.id)
            .then((refs) =>
              refs.flatMap((r) =>
                r.gcalEventId ? [[r.gcalEventId, p.displayName] as const] : [],
              ),
            )
            .catch(() => []),
        ),
      );
      setPrivateByGcalId(new Map(entries.flat()));
    } catch {
      setPartners([]);
    }
  }

  // Lazily resolve the selected partner's partner-only meetings (one cached
  // GSI-backed request). Preferred sessions need no request — they're matched
  // from the partner's preferredSessionIds below.
  React.useEffect(() => {
    const partnerId = filters.partnerId ?? "All";
    if (partnerId === "All") {
      setScopedGcalIds(null);
      return;
    }
    let cancelled = false;
    setScopedGcalIds(null);
    PartnerResource.getScopedMeetingsCached(partnerId)
      .then((refs) => {
        if (cancelled) return;
        const ids = new Set<string>();
        refs.forEach((r) => {
          if (r.gcalEventId) ids.add(r.gcalEventId);
        });
        setScopedGcalIds(ids);
      })
      .catch(() => {
        // Partner-only side unavailable — filter still matches preferred.
        if (!cancelled) setScopedGcalIds(new Set());
      });
    return () => {
      cancelled = true;
    };
  }, [filters.partnerId]);

  React.useEffect(() => {
    if (!isActiveFilters(filters)) return;

    let result = [...meetings];

    if (filters.search && filters.search !== "All") {
      result = result.filter((m) =>
        m.title?.toLowerCase().includes(filters.search!.toLowerCase()),
      );
    }

    if (filters.meetingType && filters.meetingType !== "All") {
      result = result.filter(
        (m) => m.metadata.meetingType === filters.meetingType,
      );
    }

    if (filters.status && filters.status !== "All") {
      if (filters.status === "NotVisible") {
        result = result.filter((m) => !m.metadata.memberVisible);
      }
      if (filters.status === "Missing data") {
        result = result.filter((m) => isMissingData(m));
      }
    }

    if (filters.hostId && filters.hostId !== "All") {
      result = result.filter((m) => m.host?.id === filters.hostId);
    }

    if (filters.roomKey && filters.roomKey !== "All") {
      result = result.filter((m) => effectiveRoomKey(m) === filters.roomKey);
    }

    if (filters.partnerId && filters.partnerId !== "All") {
      const partner = partners.find((p) => p.id === filters.partnerId);
      const preferred = new Set(partner?.preferredSessionIds ?? []);
      // Preferred sessions match by stable key; partner-only by gcal event id
      // (scopedGcalIds is null while the lazy fetch is in flight — preferred
      // matches show immediately and partner-only ones join when it lands).
      result = result.filter(
        (m) => preferred.has(stableKeyOf(m)) || scopedGcalIds?.has(m.id),
      );
    }

    setFilteredMeetings(result);
  }, [filters, meetings, partners, scopedGcalIds]);

  // Tick cooldown counter down every second while active
  React.useEffect(() => {
    if (cooldownRemaining <= 0) return;
    const interval = setInterval(() => {
      const remaining = getCooldownRemaining();
      setCooldownRemaining(remaining);
      if (remaining <= 0) clearInterval(interval);
    }, 1000);
    return () => clearInterval(interval);
  }, [cooldownRemaining]);

  async function handleSyncNow() {
    if (isSyncing || cooldownRemaining > 0) return;
    setIsSyncing(true);
    try {
      await MeetingResource.triggerCalendarSync();
      localStorage.setItem(SYNC_COOLDOWN_KEY, String(Date.now()));
      setCooldownRemaining(SYNC_COOLDOWN_MS);
      await loadMeetings(meetingsStart, meetingsEnd);
      setSyncSnackbar({
        open: true,
        severity: "success",
        message: "Sync complete — meetings updated.",
      });
    } catch {
      setSyncSnackbar({
        open: true,
        severity: "error",
        message: "Sync failed. Try again later.",
      });
    } finally {
      setIsSyncing(false);
    }
  }

  const noResultsMatchingFilters = activeFilters && !filteredMeetings.length;

  // The Hosts group, plus the staff of the partners the loaded meetings are
  // scoped to (a Hosts-group member who is also staff is listed once, plainly).
  const hostList = Object.values({ ...staffHostsById, ...hostsById }).map(
    (host) => ({ id: host.id, name: hostLabel(host) }),
  );
  hostList.sort((a, b) => a.name.localeCompare(b.name));

  // Distinct keys from the loaded window — no extra requests, recomputed only
  // when the meetings list changes.
  const roomKeys = React.useMemo(() => {
    const keys = new Set<string>();
    meetings.forEach((m) => {
      const key = effectiveRoomKey(m);
      if (key) keys.add(key);
    });
    return Array.from(keys).sort((a, b) => a.localeCompare(b));
  }, [meetings]);

  const partnerOptions = React.useMemo(
    () =>
      partners
        .map((p) => ({ id: p.id, name: p.displayName }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [partners],
  );

  // Stable session key → names of every partner that marked it preferred
  // (preferred is one-to-many: each partner keeps its own list).
  const preferredNamesByKey = React.useMemo(() => {
    const map = new Map<string, string[]>();
    partners.forEach((p) =>
      (p.preferredSessionIds ?? []).forEach((key) => {
        map.set(key, [...(map.get(key) ?? []), p.displayName]);
      }),
    );
    return map;
  }, [partners]);

  const cooldownSecs = Math.ceil(cooldownRemaining / 1000);
  const syncDisabled = isSyncing || cooldownRemaining > 0;

  return (
    <Stack gap={1} sx={{ marginTop: "20px", width: "100%" }}>
      {isLoading && <LinearProgress />}

      <Stack direction="row" justifyContent="flex-end" sx={{ px: 1 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={
            isSyncing ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <SyncIcon fontSize="small" />
            )
          }
          disabled={syncDisabled}
          onClick={handleSyncNow}
        >
          {isSyncing
            ? "Syncing..."
            : cooldownRemaining > 0
              ? `Sync Now (${cooldownSecs}s)`
              : "Sync Now"}
        </Button>
      </Stack>

      {!meetings.length && hasLoaded && !isLoading ? (
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary">No meetings found</Typography>
        </Box>
      ) : meetings.length > 0 ? (
        <>
          <Filters
            hosts={hostList}
            roomKeys={roomKeys}
            partners={partnerOptions}
            filters={filters}
            hasFilters={activeFilters}
            onClearFilters={() => setFilters(defaultFilters)}
            onMeetingTypeChange={(meetingTypeId: string) =>
              handleUpdateFilter(meetingTypeId, "meetingType")
            }
            onSearchChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleUpdateFilter(e.target.value, "search")
            }
            onStatusChange={(e: SelectChangeEvent) =>
              handleUpdateFilter(e.target.value, "status")
            }
            onHostChange={(e: SelectChangeEvent) => {
              handleUpdateFilter(e.target.value as string, "hostId");
            }}
            onRoomKeyChange={(e: SelectChangeEvent) => {
              handleUpdateFilter(e.target.value as string, "roomKey");
            }}
            onPartnerChange={(e: SelectChangeEvent) => {
              handleUpdateFilter(e.target.value as string, "partnerId");
            }}
          />
          <Box
            sx={{
              width: "100%",
              bgcolor: "background.paper",
              height: "100%",
            }}
          >
            {noResultsMatchingFilters ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "100%",
                  height: "100%",
                }}
              >
                <h2>No meetings found</h2>
              </Box>
            ) : (
              <MeetingList
                meetings={activeList}
                onSelectMeeting={handleSelectMeeting}
                selectedMeetingId={selectedMeeting?.id}
                privateByGcalId={privateByGcalId}
                preferredNamesByKey={preferredNamesByKey}
              />
            )}
          </Box>

          <Dialog
            open={selectedMeeting !== undefined}
            onClose={handleCloseDetails}
            maxWidth="md"
            fullWidth
            fullScreen={isMobile}
          >
            <DialogTitle
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <span>Meeting Details</span>
              <IconButton
                size="small"
                onClick={handleCloseDetails}
                sx={{ flexShrink: 0 }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              {selectedMeeting && (
                <MeetingDetailsContainer
                  key={selectedMeeting.id}
                  selectedMeeting={selectedMeeting}
                />
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDetails}>Close</Button>
            </DialogActions>
          </Dialog>
        </>
      ) : null}

      <Snackbar
        open={syncSnackbar.open}
        autoHideDuration={4000}
        onClose={() => setSyncSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={syncSnackbar.severity}
          onClose={() => setSyncSnackbar((s) => ({ ...s, open: false }))}
        >
          {syncSnackbar.message}
        </Alert>
      </Snackbar>
    </Stack>
  );
};

export const CalendarManagement = () => {
  return <MeetingListView />;
};
